import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Sparkles, Check, X, ArrowRight, Trophy, Zap, RotateCcw, ChevronLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

export default function QuizPlayer({ quizId, onExit }) {
  const { user } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/quizzes/${quizId}`)
      .then((r) => {
        setQuiz(r.data);
        setAnswers(new Array(r.data.questions.length).fill(-1));
      })
      .catch(() => toast.error("Could not load quiz"))
      .finally(() => setLoading(false));
  }, [quizId]);

  const total = quiz?.questions?.length || 0;
  const current = quiz?.questions?.[idx];
  const chosen = answers[idx];
  const canNext = chosen !== -1 && chosen !== undefined;
  const progress = useMemo(() => (total ? ((idx + 1) / total) * 100 : 0), [idx, total]);

  const choose = (i) => {
    setAnswers((a) => {
      const next = [...a];
      next[idx] = i;
      return next;
    });
  };

  const submit = async () => {
    if (!user) return toast.error("Sign in to submit");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/quizzes/${quizId}/attempt`, { answers });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setResult(null);
    setIdx(0);
    setAnswers(new Array(total).fill(-1));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading quiz…
      </div>
    );
  }
  if (!quiz) return null;

  // Result screen
  if (result) {
    return <QuizResult quiz={quiz} result={result} onRestart={restart} onExit={onExit} />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-6" data-testid="quiz-player">
        <button
          onClick={onExit}
          data-testid="quiz-exit-btn"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ChevronLeft className="w-4 h-4" /> Exit quiz
        </button>

        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass mb-4">
          <Sparkles className="w-3 h-3 text-secondary" />
          <span className="text-[10px] uppercase tracking-widest font-semibold">
            {quiz.subject} · {quiz.chapter || "Mixed"} · +{quiz.xp_reward} XP
          </span>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">{quiz.title}</h1>

        <div className="mt-6">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
            <span>
              Question <b className="text-foreground">{idx + 1}</b> of {total}
            </span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <motion.div
              className="h-full bg-primary"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.35, ease: "easeOut" }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="mt-8 glass rounded-3xl p-8"
          >
            <div className="text-lg md:text-xl font-semibold leading-relaxed">{current.question}</div>
            <div className="mt-6 space-y-3">
              {current.options.map((opt, i) => {
                const selected = chosen === i;
                return (
                  <button
                    key={i}
                    onClick={() => choose(i)}
                    data-testid={`quiz-option-${i}`}
                    className={`w-full text-left p-4 rounded-2xl border-2 transition-all group ${
                      selected
                        ? "border-primary bg-primary/10"
                        : "border-transparent bg-muted/50 hover:bg-muted"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                          selected
                            ? "bg-primary text-primary-foreground"
                            : "bg-background border border-border text-muted-foreground"
                        }`}
                      >
                        {String.fromCharCode(65 + i)}
                      </div>
                      <div className="text-sm md:text-base font-medium">{opt}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-8 flex items-center justify-between">
          <Button
            variant="outline"
            disabled={idx === 0}
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            data-testid="quiz-prev-btn"
            className="rounded-full h-11 px-5"
          >
            Previous
          </Button>
          {idx === total - 1 ? (
            <Button
              onClick={submit}
              disabled={!canNext || submitting}
              data-testid="quiz-submit-btn"
              className="rounded-full h-11 px-6 bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {submitting ? "Submitting…" : "Submit answers"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
              disabled={!canNext}
              data-testid="quiz-next-btn"
              className="rounded-full h-11 px-6 bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function QuizResult({ quiz, result, onRestart, onExit }) {
  const { percent, score, total, passed, xp_earned, breakdown, certificate } = result;
  return (
    <div className="min-h-screen bg-background text-foreground pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-6" data-testid="quiz-result">
        <div
          className={`relative rounded-3xl overflow-hidden p-10 text-center glass ${
            passed ? "border-emerald-500/40" : "border-amber-500/40"
          } border-2`}
        >
          <div
            className={`absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full blur-3xl ${
              passed ? "bg-emerald-500/20" : "bg-amber-500/20"
            }`}
          />
          <div className="relative">
            <div
              className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center ${
                passed ? "bg-emerald-500/15" : "bg-amber-500/15"
              }`}
            >
              <Trophy className={`w-9 h-9 ${passed ? "text-emerald-500" : "text-amber-500"}`} />
            </div>
            <div className="mt-4 text-xs uppercase tracking-widest font-semibold text-muted-foreground">
              {passed ? "Certificate earned" : "Nice try — keep going"}
            </div>
            <div className="font-display text-6xl font-black mt-3" data-testid="quiz-percent">
              {percent}%
            </div>
            <div className="text-muted-foreground mt-1">
              {score} / {total} correct
            </div>
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass">
              <Zap className="w-4 h-4 text-orange-500" />
              <span className="text-sm font-semibold" data-testid="quiz-xp-earned">
                +{xp_earned} XP
              </span>
            </div>
          </div>
        </div>

        {certificate && (
          <div className="mt-8 rounded-3xl p-8 bg-gradient-to-br from-emerald-500/10 via-cyan-500/10 to-indigo-500/10 border border-emerald-500/30">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-600 dark:text-emerald-400">
              Luminon certificate
            </div>
            <div className="font-display text-2xl font-bold mt-1">{certificate.quiz_title}</div>
            <div className="text-sm text-muted-foreground mt-2">
              Awarded to <b>{certificate.user_id}</b> for scoring <b>{certificate.percent}%</b> on{" "}
              {new Date(certificate.issued_at).toLocaleDateString()}.
            </div>
          </div>
        )}

        <div className="mt-8 space-y-3" data-testid="quiz-breakdown">
          {breakdown.map((b, i) => (
            <div
              key={i}
              className={`glass rounded-2xl p-5 border ${
                b.is_correct ? "border-emerald-500/30" : "border-rose-500/30"
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                    b.is_correct ? "bg-emerald-500/20 text-emerald-500" : "bg-rose-500/20 text-rose-500"
                  }`}
                >
                  {b.is_correct ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold leading-snug">{b.question}</div>
                  {!b.is_correct && (
                    <div className="text-xs mt-1 text-muted-foreground">
                      Correct answer:{" "}
                      <b>{quiz.questions[i]?.options?.[b.correct_index]}</b>
                    </div>
                  )}
                  {b.explanation && (
                    <div className="mt-2 text-xs text-muted-foreground italic">
                      {b.explanation}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            onClick={onRestart}
            data-testid="quiz-retry-btn"
            className="rounded-full h-11 px-5"
          >
            <RotateCcw className="w-4 h-4 mr-2" /> Try again
          </Button>
          <Button
            onClick={onExit}
            data-testid="quiz-exit-result-btn"
            className="rounded-full h-11 px-6 bg-primary hover:bg-primary/90"
          >
            Back to quizzes
          </Button>
        </div>
      </div>
    </div>
  );
}
